import { store } from "../core/store";
import { socket } from "../core/socket";
import { showToast } from "../core/toast";
import { listPresetNames, savePreset as saveLocalPreset, loadPreset as loadLocalPreset } from "../core/presets";
import { buildNumberField, buildCheckboxField, type FieldSpec } from "./formFields";
import { GameState, type AchievementKey, type GameConfig } from "../../shared/types";

// Everything here is the "expert" tier - fields common enough to need editing
// from inside a game (rounds, timers players actually feel, scoring tiers,
// difficulty, re-rolls, hints, CCC schedule, ...) live directly in the Lobby's
// inline settings grid instead (src/client/ui/lobby.ts) so the host doesn't
// need to open a modal for them and everyone can see them at a glance.

const TIMER_FIELDS: FieldSpec[] = [
  { path: ["timers", "wordSelectionSeconds"], label: "Word Selection (s)" },
  { path: ["timers", "guessTimerFloorSeconds"], label: "Guess timer floor (s)" },
  { path: ["timers", "artistReconnectGraceSeconds"], label: "Artist reconnect grace (s)" },
  { path: ["timers", "roundStartDisplaySeconds"], label: "Round start display (s)" },
  { path: ["timers", "artistSelectionDisplaySeconds"], label: "Artist selection display (s)" },
  { path: ["timers", "scoreCalculationDisplaySeconds"], label: "Score calc display (s)" },
  { path: ["timers", "nextArtistDisplaySeconds"], label: "Next artist display (s)" },
  { path: ["timers", "victoryRevealDelaySeconds"], label: "Victory reveal delay (s)", step: 0.1 },
  { path: ["timers", "achievementStartDelaySeconds"], label: "Achievements: delay before first (s)", step: 0.1 },
  { path: ["timers", "achievementPlayerRevealDelaySeconds"], label: "Achievements: delay before players show (s)", step: 0.1 },
  { path: ["timers", "achievementDotIntervalSeconds"], label: "Achievements: delay between players (s)", step: 0.05 },
  { path: ["timers", "achievementNextDelaySeconds"], label: "Achievements: delay before next (s)", step: 0.1 },
  { path: ["timers", "disconnectRemovalSeconds"], label: "Remove disconnected players after (s)" },
];

const SCORING_FIELDS: FieldSpec[] = [
  { path: ["scoring", "artistFirstGuess"], label: "Artist: First Guess" },
  { path: ["scoring", "artistAdditionalGuess"], label: "Artist: Additional Guess" },
  { path: ["scoring", "playerFirst"], label: "1st Player to Guess" },
  { path: ["scoring", "playerSecond"], label: "2nd Player to Guess" },
  { path: ["scoring", "playerThird"], label: "3rd Player to Guess" },
  { path: ["scoring", "playerRemaining"], label: "Every Additional Guess" },
  { path: ["scoring", "decayAmount"], label: "Decay per interval" },
  { path: ["scoring", "decayIntervalSeconds"], label: "Decay interval (s)" },
  { path: ["scoring", "redrawPenalty"], label: "Re-roll penalty" },
];

const CHAT_FIELDS: FieldSpec[] = [
  { path: ["chat", "historyLimit"], label: "Chat history limit" },
  { path: ["chat", "guessCloseThreshold"], label: "Guess closeness threshold" },
  { path: ["chat", "inactivityWipeMinutes"], label: "Wipe chat after inactivity (min)" },
];

const ACHIEVEMENT_LABELS: Record<AchievementKey, string> = {
  iGotYou: "I got you!",
  picasso: "Picasso",
  isThatABird: "Is that a bird? No? Oh...",
  consistency100: "100% consistency",
  worthATry: "Worth a try.",
  perfection: "PERFECTION!",
  speedingTicket: "Speeding Ticket",
  firstTry: "First try!",
  purity: "Purity",
  closeButNoCigar: "Close, but no Cigar",
  quietType: "The quiet type",
  photoFinish: "Photo-Finish",
};

// Client-side mirror of src/server/state/cccCatalog.ts's CCC_CATALOG - server-only
// data (see that file's own doc comment), so the id/label pairs here must be kept
// in sync by hand whenever a card family is added, removed, or renamed there.
// The id (`${familyId}:${level}`) is the exact string format the server expects
// in ccc.disabledCards.
const CCC_CARD_LIST: { id: string; label: string }[] = [
  { id: "miscommunication:1", label: "Miscommunication" },
  { id: "stress:1", label: "Stress I" },
  { id: "stress:2", label: "Stress II" },
  { id: "noReturn:1", label: "No Return" },
  { id: "crossingTheLine:1", label: "Crossing the Line" },
  { id: "colorblind:1", label: "Colorblind" },
  { id: "pixelated:1", label: "Pixelated I" },
  { id: "pixelated:2", label: "Pixelated II" },
  { id: "pixelated:3", label: "Pixelated III" },
  { id: "monochrome:1", label: "Monochrome I" },
  { id: "monochrome:2", label: "Monochrome II" },
  { id: "upsideDown:1", label: "Upside Down I" },
  { id: "upsideDown:2", label: "Upside Down II" },
  { id: "blindness:1", label: "Blindness I" },
  { id: "blindness:2", label: "Blindness II" },
  { id: "schizophrenia:1", label: "Schizophrenia I" },
  { id: "schizophrenia:2", label: "Schizophrenia II" },
  { id: "forgetfulness:1", label: "Forgetfulness I" },
  { id: "forgetfulness:2", label: "Forgetfulness II" },
  { id: "oneLineMan:1", label: "One Line Man" },
  { id: "lag:1", label: "Lag I" },
  { id: "lag:2", label: "Lag II" },
  { id: "tremble:1", label: "Tremble I" },
  { id: "tremble:2", label: "Tremble II" },
  { id: "tremble:3", label: "Tremble III" },
  { id: "claustrophobia:1", label: "Claustrophobia I" },
  { id: "claustrophobia:2", label: "Claustrophobia II" },
  { id: "confusion:1", label: "Confusion I" },
  { id: "confusion:2", label: "Confusion II" },
  { id: "theGambler:1", label: "The Gambler" },
  { id: "hazy:1", label: "Hazy I" },
  { id: "hazy:2", label: "Hazy II" },
  { id: "hazy:3", label: "Hazy III" },
];

export function initSettingsPanel(): void {
  const overlay = document.getElementById("settings-overlay")!;
  const modal = document.getElementById("settings-modal")!;
  const settingsBtn = document.getElementById("settings-btn") as HTMLButtonElement;

  let draft: GameConfig | null = null;
  const getDraft = () => draft;
  /** Whether the open modal was last rendered in its locked (game in progress)
   * form - so it re-renders the moment a game starts or ends while it's open. */
  let renderedLocked: boolean | null = null;

  function isLocked(): boolean {
    const snapshot = store.getState().snapshot;
    return !!snapshot && snapshot.state !== GameState.WAITING_ROOM;
  }

  function closeOverlay(): void {
    overlay.classList.add("hidden");
    draft = null;
  }

  settingsBtn.addEventListener("click", () => {
    overlay.classList.remove("hidden");
    // Always open on the lobby's current settings - a draft left over from
    // earlier could predate changes made in the Lobby's own settings grid, and
    // applying it would quietly undo them.
    draft = null;
    render();
  });
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) closeOverlay();
  });

  function render(): void {
    const state = store.getState();
    const locked = isLocked();
    renderedLocked = locked;
    // While locked there's nothing to edit, so always show the live settings.
    if ((!draft || locked) && state.config) draft = JSON.parse(JSON.stringify(state.config));
    const isHost = state.isHost;
    // Settings are frozen for the whole game, host or not - see the server's
    // matching check in hostHandlers.ts.
    const canEdit = isHost && !locked;

    modal.innerHTML = "";

    const header = document.createElement("div");
    header.className = "settings-header";
    const title = document.createElement("h2");
    title.textContent = "Expert Settings";
    const closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "close-button";
    closeBtn.textContent = "✕";
    closeBtn.addEventListener("click", () => closeOverlay());
    header.append(title, closeBtn);
    modal.appendChild(header);

    if (!draft) {
      const loading = document.createElement("p");
      loading.textContent = "Loading configuration...";
      modal.appendChild(loading);
      return;
    }

    const intro = document.createElement("p");
    intro.className = "hint-text";
    intro.textContent = locked
      ? "Read-only while a game is in progress. Settings can be changed again once everyone is back in the Lobby."
      : isHost
        ? "Less commonly changed settings. The main gameplay options are in the Lobby itself."
        : "Read-only. Only the host can change settings.";
    modal.appendChild(intro);

    if (canEdit) {
      const applyWarning = document.createElement("p");
      applyWarning.className = "settings-apply-warning";
      applyWarning.textContent = "You need to apply any changes you make in here using the button at the bottom!";
      modal.appendChild(applyWarning);
    }

    const body = document.createElement("div");
    body.className = "settings-body";

    body.appendChild(buildSection("Timers", TIMER_FIELDS, canEdit));
    body.appendChild(buildSection("Scoring", SCORING_FIELDS, canEdit));
    body.appendChild(buildSection("Chat", CHAT_FIELDS, canEdit));
    body.appendChild(buildWordFilterSection(canEdit));
    body.appendChild(buildCCCSection(canEdit));
    body.appendChild(buildCCCCardsSection(canEdit));
    body.appendChild(buildAchievementsSection(canEdit));

    modal.appendChild(body);

    if (isHost) {
      modal.appendChild(buildHostControls(canEdit));
    }
  }

  function buildSection(title: string, fields: FieldSpec[], canEdit: boolean): HTMLElement {
    const section = document.createElement("fieldset");
    section.className = "settings-section";
    const legend = document.createElement("legend");
    legend.textContent = title;
    section.appendChild(legend);
    for (const field of fields) {
      section.appendChild(buildNumberField(field, canEdit, getDraft));
    }
    return section;
  }

  function buildCollapsibleSection(title: string): { details: HTMLDetailsElement; body: HTMLElement } {
    const details = document.createElement("details");
    details.className = "settings-collapsible";
    const summary = document.createElement("summary");
    summary.textContent = title;
    details.appendChild(summary);
    const body = document.createElement("div");
    body.className = "settings-collapsible-body";
    details.appendChild(body);
    return { details, body };
  }

  function buildCCCSection(canEdit: boolean): HTMLElement {
    const section = document.createElement("fieldset");
    section.className = "settings-section";
    const legend = document.createElement("legend");
    legend.textContent = "Chaos Canvas Cards";
    section.appendChild(legend);

    section.appendChild(
      buildCheckboxField(
        ["ccc", "allowDirectHighLevelPickup"],
        "Allow level 2+ cards to appear in direct pickups (not just upgrades)",
        canEdit,
        getDraft
      )
    );

    // Not host-toggleable in a normal deployment - see ccc.debugMode's env-gating
    // in docs/CONFIGURATION.md. Only rendered at all when the server has opted in.
    if (store.getState().cccDebugAvailable) {
      section.appendChild(
        buildCheckboxField(
          ["ccc", "debugMode"],
          "Debug mode: host sees every card and can pick up to 2 directly (testing aid)",
          canEdit,
          getDraft
        )
      );
    }

    return section;
  }

  function buildCCCCardsSection(canEdit: boolean): HTMLElement {
    const { details, body } = buildCollapsibleSection("Chaos Canvas Cards: Enabled/Disabled");

    const note = document.createElement("p");
    note.className = "hint-text";
    note.textContent = "Unchecking a card removes it from future offers - copies players already picked up aren't taken away.";
    body.appendChild(note);

    for (const { id, label } of CCC_CARD_LIST) {
      const row = document.createElement("label");
      row.className = "achievement-field";

      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.checked = !(draft?.ccc.disabledCards ?? []).includes(id);
      checkbox.disabled = !canEdit;
      checkbox.addEventListener("change", () => {
        if (!draft) return;
        const disabled = new Set(draft.ccc.disabledCards);
        if (checkbox.checked) disabled.delete(id);
        else disabled.add(id);
        draft.ccc.disabledCards = Array.from(disabled);
      });

      row.append(checkbox, document.createTextNode(" " + label));
      body.appendChild(row);
    }

    return details;
  }

  function buildWordFilterSection(canEdit: boolean): HTMLElement {
    const section = document.createElement("fieldset");
    section.className = "settings-section";
    const legend = document.createElement("legend");
    legend.textContent = "Chat Word Filter";
    section.appendChild(legend);

    const note = document.createElement("p");
    note.className = "hint-text";
    note.textContent = "The on/off toggle for this lives in the Lobby - these are the extra words to also mask.";
    section.appendChild(note);

    const customLabel = document.createElement("div");
    customLabel.className = "field-label";
    customLabel.textContent = "Extra words to filter (comma-separated)";
    section.appendChild(customLabel);

    const customInput = document.createElement("textarea");
    customInput.rows = 2;
    customInput.value = (draft?.chat.wordFilter.customWords ?? []).join(", ");
    customInput.disabled = !canEdit;
    customInput.addEventListener("change", () => {
      if (!draft) return;
      draft.chat.wordFilter.customWords = customInput.value
        .split(",")
        .map((w) => w.trim())
        .filter((w) => w.length > 0);
    });
    section.appendChild(customInput);

    return section;
  }

  function buildAchievementsSection(canEdit: boolean): HTMLElement {
    const { details, body } = buildCollapsibleSection("Bonus Achievements");

    for (const key of Object.keys(ACHIEVEMENT_LABELS) as AchievementKey[]) {
      const row = document.createElement("div");
      row.className = "achievement-field";

      const checkboxLabel = document.createElement("label");
      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.checked = !!draft?.achievements[key]?.enabled;
      checkbox.disabled = !canEdit;
      checkbox.addEventListener("change", () => {
        if (draft) draft.achievements[key].enabled = checkbox.checked;
      });
      checkboxLabel.append(checkbox, document.createTextNode(" " + ACHIEVEMENT_LABELS[key]));

      const pointsInput = document.createElement("input");
      pointsInput.type = "number";
      pointsInput.value = String(draft?.achievements[key]?.points ?? 0);
      pointsInput.disabled = !canEdit;
      pointsInput.addEventListener("change", () => {
        if (draft) draft.achievements[key].points = Number(pointsInput.value) || 0;
      });

      row.append(checkboxLabel, pointsInput);
      body.appendChild(row);
    }
    return details;
  }

  /** `canEdit` false (a game is in progress) leaves only the lobby-management
   * buttons - applying settings or loading a preset isn't possible mid-game. */
  function buildHostControls(canEdit: boolean): HTMLElement {
    const controls = document.createElement("div");
    controls.className = "settings-host-controls";
    if (canEdit) appendSettingsControls(controls);
    appendDangerRow(controls);
    return controls;
  }

  function appendSettingsControls(controls: HTMLElement): void {

    const applyBtn = document.createElement("button");
    applyBtn.type = "button";
    applyBtn.className = "primary-button";
    applyBtn.textContent = "Apply Changes";
    applyBtn.addEventListener("click", () => {
      if (!draft) return;
      // host:replaceConfig (wholesale swap-in), not host:updateConfig (merge onto
      // current) - this button is also how a loaded preset gets applied, and an
      // older preset can be missing fields added since it was saved. A merge
      // would silently leave those untouched instead of resetting them to the
      // preset's own value; for a normal (non-preset) edit, draft is always a
      // complete clone of the current config already, so this behaves the same
      // either way.
      socket.emit("host:replaceConfig", draft, (ok) => {
        showToast(ok ? "Settings applied successfully." : "Failed to apply settings.", ok ? "success" : "error");
      });
    });
    controls.appendChild(applyBtn);

    // Presets live only in this browser's localStorage, not on the server - see
    // core/presets.ts. This is the "expert" preset store, independent from the
    // Lobby's own preset row (which has its own "lobby"-scoped store) - saved
    // here, a preset only shows up back here. Saving captures the form's
    // current (possibly unapplied) draft; loading fills the draft back in,
    // still requiring "Apply Changes" to actually take effect.
    const presetsHeading = document.createElement("p");
    presetsHeading.className = "settings-presets-heading";
    presetsHeading.textContent = "Expert Setting Presets";
    controls.appendChild(presetsHeading);

    const presetRow = document.createElement("div");
    presetRow.className = "preset-row";

    const presetNameInput = document.createElement("input");
    presetNameInput.type = "text";
    presetNameInput.placeholder = "Preset name";
    const saveBtn = document.createElement("button");
    saveBtn.type = "button";
    saveBtn.className = "secondary-button";
    saveBtn.textContent = "Save Preset";
    saveBtn.addEventListener("click", () => {
      const name = presetNameInput.value.trim();
      if (!name || !draft) return;
      saveLocalPreset("expert", name, draft);
      showToast(`Preset "${name}" saved.`, "success");
      render();
    });
    presetRow.append(presetNameInput, saveBtn);
    controls.appendChild(presetRow);

    const loadRow = document.createElement("div");
    loadRow.className = "preset-row";
    const presetSelect = document.createElement("select");
    for (const name of listPresetNames("expert")) {
      const option = document.createElement("option");
      option.value = name;
      option.textContent = name;
      presetSelect.appendChild(option);
    }
    const loadBtn = document.createElement("button");
    loadBtn.type = "button";
    loadBtn.className = "secondary-button";
    loadBtn.textContent = "Load Preset";
    loadBtn.addEventListener("click", () => {
      const name = presetSelect.value;
      if (!name) return;
      const loaded = loadLocalPreset("expert", name);
      if (loaded) {
        draft = loaded;
        render();
        showToast(`Preset "${name}" loaded. Apply to use it.`, "success");
      } else {
        showToast("Failed to load preset.", "error");
      }
    });
    loadRow.append(presetSelect, loadBtn);
    controls.appendChild(loadRow);
  }

  function appendDangerRow(controls: HTMLElement): void {
    const dangerRow = document.createElement("div");
    dangerRow.className = "danger-row";
    const returnBtn = document.createElement("button");
    returnBtn.type = "button";
    returnBtn.className = "secondary-button";
    returnBtn.textContent = "Return Everyone to Lobby";
    returnBtn.addEventListener("click", () => {
      if (confirm("This ends the current game for everyone and returns the whole lobby to the pre-game screen. Continue?")) {
        socket.emit("host:returnToWaitingRoom");
      }
    });
    dangerRow.appendChild(returnBtn);

    const resetBtn = document.createElement("button");
    resetBtn.type = "button";
    resetBtn.className = "danger-button";
    resetBtn.textContent = "Reset Lobby";
    resetBtn.addEventListener("click", () => {
      if (confirm("This disconnects everyone and wipes this lobby's players. Continue?")) {
        socket.emit("host:resetLobby");
      }
    });
    dangerRow.appendChild(resetBtn);
    controls.appendChild(dangerRow);
  }

  store.subscribe((state) => {
    if (!draft && state.config) {
      render();
      return;
    }
    // A game starting (or ending) while the modal is open flips it between
    // editable and read-only straight away, rather than leaving stale controls.
    if (!overlay.classList.contains("hidden") && renderedLocked !== null && isLocked() !== renderedLocked) render();
  });
}
